'use client';

import { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@repo/ui/components/tooltip';
import { cn } from '@repo/ui/lib/utils';

/**
 * The house copy-to-clipboard affordance for an on-chain address.
 *
 * Truncated addresses are everywhere in this app, and every one of them
 * must hand over the full string on demand — an operator reconciling a
 * holder, a strategist saving their vault address. This is that control,
 * once: icon button, a moment of Check feedback after a successful copy,
 * a touch target well past the drawn 14px icon, and a visible keyboard
 * focus ring. A blocked clipboard (permissions, insecure context) is not
 * an error worth interrupting anyone over — the address stays readable
 * wherever this button sits.
 */

/**
 * Expands the tappable area well past the drawn icon: the 14px glyph plus
 * a 16px inset on every side is a 46px hit box — over the 44px floor.
 */
const TOUCH_TARGET =
  "relative after:absolute after:-inset-4 after:content-['']";

interface CopyAddressButtonProps {
  address: string;
  /**
   * What the reader is copying, for assistive tech — "the vault address",
   * "the full address 0xabc…". Defaults to the address itself.
   */
  label?: string;
  /**
   * What is announced once copied. "Address copied" by default; a value
   * that is not an address (a transaction or configuration hash) names
   * itself, so the announcement is not a wrong word.
   */
  copiedLabel?: string;
  /** Show the hover tooltip (off where the surrounding text already says it). */
  tooltip?: boolean;
  className?: string;
}

export function CopyAddressButton({
  address,
  label,
  copiedLabel = 'Address copied',
  tooltip = false,
  className
}: CopyAddressButtonProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
    } catch {
      // Nothing to report: the address is still readable in place.
    }
  };

  const what = label ?? `the full address ${address}`;
  const button = (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? copiedLabel : `Copy ${what}`}
      className={cn(
        'text-rowa-text-tertiary hover:text-rowa-text-primary rounded-rowa-sm focus-visible:ring-rowa-border-accent inline-flex shrink-0 cursor-pointer items-center transition-colors focus-visible:ring-2 focus-visible:outline-none',
        TOUCH_TARGET,
        className
      )}
    >
      {copied ? (
        <Check aria-hidden className="text-rowa-status-positive size-3.5" />
      ) : (
        <Copy aria-hidden className="size-3.5" />
      )}
    </button>
  );

  if (!tooltip) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{copied ? 'Copied' : `Copy ${what}`}</TooltipContent>
    </Tooltip>
  );
}
